import { FC, useMemo, useState } from 'react';
import {
  Box,
  Flex,
  FormControl,
  FormErrorMessage,
  FormLabel,
  Input,
  List,
  ListItem,
  SystemStyleObject,
  Tag,
  TagCloseButton,
  TagLabel,
} from '@chakra-ui/react';
import { useCombobox, useMultipleSelection } from 'downshift';
import { isEmpty } from 'ramda';

import { InfoPopover } from '../user-details/components/info-popover';
import { filterOptions } from './filter-options';

export type Option = {
  label: string;
  value: string;
  info?: string;
};

type MultipleComboboxProps = {
  label: string;
  options: Option[];
  selectedOptions?: Option[];
  onChange: (selectedItems: Option[]) => void;
  isInvalid?: boolean;
  errorMessage?: string;
  isRequired?: boolean;
  testId?: string;
  sx?: SystemStyleObject;
};

// Docs: https://www.downshift-js.com/use-multiple-selection#usage-with-combobox
export const MultiCombobox: FC<MultipleComboboxProps> = ({
  label,
  options,
  selectedOptions,
  onChange,
  isInvalid,
  errorMessage,
  isRequired,
  testId,
  sx,
}) => {
  const [inputValue, setInputValue] = useState('');
  const [selectedItems, setSelectedItems] = useState<Option[]>(
    selectedOptions || []
  );
  const [showFocusRing, setShowFocusRing] = useState<boolean>(false);

  const items = useMemo(
    () => filterOptions(options, selectedItems, inputValue),
    [options, selectedItems, inputValue]
  );

  const {
    getSelectedItemProps,
    getDropdownProps,
    removeSelectedItem,
  } = useMultipleSelection({
    selectedItems,
    onStateChange({ selectedItems: newSelectedItems, type }) {
      switch (type) {
        // When a selected item is removed:
        case useMultipleSelection.stateChangeTypes.SelectedItemKeyDownBackspace:
        case useMultipleSelection.stateChangeTypes.SelectedItemKeyDownDelete:
        case useMultipleSelection.stateChangeTypes.DropdownKeyDownBackspace:
        case useMultipleSelection.stateChangeTypes.FunctionRemoveSelectedItem:
          setSelectedItems(newSelectedItems || []); // set local state
          onChange(newSelectedItems || []); // set parent's state
          break;
        default:
          break;
      }
    },
  });

  const {
    isOpen,
    getLabelProps,
    getInputProps,
    highlightedIndex,
    getMenuProps,
    getItemProps,
  } = useCombobox({
    items,
    itemToString(item) {
      return item ? item.value : '';
    },
    defaultHighlightedIndex: 0, // after selection, highlight the first item.
    selectedItem: null,
    stateReducer(_state, actionAndChanges) {
      const { changes, type } = actionAndChanges;

      switch (type) {
        case useCombobox.stateChangeTypes.InputKeyDownEnter:
        case useCombobox.stateChangeTypes.ItemClick:
          return {
            ...changes,
            isOpen: true, // keep the menu open after selection.
            highlightedIndex: 0, // with the first option highlighted.
          };
        default:
          return changes;
      }
    },
    onStateChange({
      inputValue: newInputValue,
      type,
      selectedItem: newSelectedItem,
    }) {
      switch (type) {
        // When the user selects an item:
        case useCombobox.stateChangeTypes.InputKeyDownEnter:
        case useCombobox.stateChangeTypes.ItemClick:
        case useCombobox.stateChangeTypes.InputBlur:
          if (newSelectedItem) {
            setSelectedItems([...selectedItems, newSelectedItem]); // set local state
            onChange([...selectedItems, newSelectedItem]); // set parent's state
            setInputValue(''); // reset the input
          }
          break;

        // When the user types:
        case useCombobox.stateChangeTypes.InputChange:
          setInputValue(newInputValue || '');
          break;

        default:
          break;
      }
    },
  });
  return (
    <div>
      <FormControl isInvalid={isInvalid} isRequired={isRequired} sx={sx}>
        <FormLabel {...getLabelProps()}>{label}</FormLabel>

        <Flex
          p={2}
          gridGap={2}
          borderWidth={isInvalid ? '2px' : '1px'}
          borderColor={isInvalid ? 'red.500' : 'gray.200'}
          outline={showFocusRing ? '2px solid' : undefined}
          outlineColor="blue.500"
          outlineOffset="-2px"
          borderRadius="md"
          wrap="wrap"
          data-testid="selected-option-list"
        >
          {selectedItems.map((selectedItemForRender, index) => {
            return (
              <Tag
                key={`selected-${selectedItemForRender.value}`}
                size="lg"
                data-testid="selected-option"
                {...getSelectedItemProps({
                  selectedItem: selectedItemForRender,
                  index,
                })}
              >
                <TagLabel>{selectedItemForRender.label}</TagLabel>
                {selectedItemForRender.info && (
                  <Flex mb="1px" ml={2}>
                    <InfoPopover
                      header={`${selectedItemForRender.label} policy`}
                      info={selectedItemForRender.info}
                    />
                  </Flex>
                )}
                <TagCloseButton
                  mt="1px"
                  onClick={(e) => {
                    e.stopPropagation();
                    removeSelectedItem(selectedItemForRender);
                  }}
                />
              </Tag>
            );
          })}

          <Input
            flex={1}
            variant="unstyled"
            onFocusCapture={() => {
              setShowFocusRing(true);
            }}
            onBlurCapture={() => setShowFocusRing(false)}
            data-testid={testId}
            {...getInputProps(getDropdownProps({ preventKeyAction: isOpen }))}
          />
        </Flex>

        {isInvalid && errorMessage && (
          <FormErrorMessage data-testid={`${testId}-error-msg`}>
            {errorMessage}
          </FormErrorMessage>
        )}
      </FormControl>

      <Box {...getMenuProps()}>
        {isOpen && !isEmpty(items) && (
          <List
            p={2}
            border="1px solid"
            borderColor="gray.300"
            borderRadius="md"
            maxHeight="120px"
            overflowY="scroll"
            shadow="lg"
            data-testid="option-list"
          >
            {items.map((item, index) => (
              <ListItem
                key={`option-${item.value}`}
                px={3}
                py={2}
                bg={highlightedIndex === index ? 'blue.50' : 'inherit'}
                data-testid="option"
                {...getItemProps({ item, index })}
              >
                {item.label}
              </ListItem>
            ))}
          </List>
        )}
      </Box>
    </div>
  );
};
